import React, { useEffect, useMemo, useRef } from "react";
import { FaEdit, FaTrash, FaSlidersH } from "react-icons/fa";
import Button from "../../../components/Button/Button";

const InvitedClubTable = ({
  items,                // lignes VISIBLES (page courante)
  loading,
  selectedIds,
  onToggleSelect,       // (id) => void
  onToggleSelectAll,    // () => void  -> sélection/désélection des lignes visibles
  allVisibleSelected,   // boolean
  onEdit,
  onDelete,
  onManage,
  page,
  setPage,
  limit,
  total,
}) => {
  const totalPages = Math.max(1, Math.ceil((total || 0) / (limit || 1)));
  const showPagination = total > limit;

  // ids visibles (à partir de items déjà paginés)
  const visibleIds = useMemo(() => items.map((i) => i.id), [items]);

  // état "indeterminate" : au moins 1 visible coché, mais pas tous
  const someVisibleSelected = useMemo(() => {
    const nbSelectedVisible = visibleIds.filter((id) =>
      selectedIds.includes(id)
    ).length;
    return nbSelectedVisible > 0 && !allVisibleSelected;
  }, [visibleIds, selectedIds, allVisibleSelected]);

  // ref pour checkbox header (indeterminate)
  const headerCbRef = useRef(null);
  useEffect(() => {
    if (headerCbRef.current) {
      headerCbRef.current.indeterminate = someVisibleSelected;
    }
  }, [someVisibleSelected]);

  return (
    <div className="rounded-2xl border border-gray-200/70 dark:border-slate-700/70 bg-white/80 dark:bg-slate-800/70 backdrop-blur shadow-sm overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 dark:bg-slate-900/50">
            <tr>
              <th className="px-4 py-3 text-left w-12">
                <input
                  ref={headerCbRef}
                  type="checkbox"
                  checked={allVisibleSelected}
                  onChange={onToggleSelectAll}
                  aria-label="Sélectionner toutes les lignes visibles"
                />
              </th>
              <th className="px-4 py-3 text-left">Nom du club</th>
              <th className="px-4 py-3 text-left">Email</th>
              <th className="px-4 py-3 text-left">Téléphone</th>
              <th className="px-4 py-3 text-left">Actions</th>
            </tr>
          </thead>

          <tbody>
            {loading && (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-gray-500">
                  Chargement…
                </td>
              </tr>
            )}

            {!loading && items.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-gray-500">
                  Aucun club.
                </td>
              </tr>
            )}

            {!loading &&
              items.map((club) => (
                <tr
                  key={club.id}
                  className="border-t border-gray-100 dark:border-slate-700/50 hover:bg-gray-50/60 dark:hover:bg-slate-700/40 transition"
                >
                  <td className="px-4 py-3 align-middle">
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(club.id)}
                      onChange={() => onToggleSelect(club.id)}
                      aria-label={`Sélectionner ${club.nom_club}`}
                    />
                  </td>
                  <td className="px-4 py-3 align-middle font-medium text-gray-900 dark:text-white">
                    {club.nom_club}
                  </td>
                  <td className="px-4 py-3 align-middle">
                    {club.mail_club || "—"}
                  </td>
                  <td className="px-4 py-3 align-middle">
                    {club.tel_club || "—"}
                  </td>
                  <td className="px-4 py-3 align-middle">
                    <div className="flex flex-wrap gap-2">
                      <Button
                        variant="soft"
                        size="xs"
                        icon={FaEdit}
                        onClick={() => onEdit(club)}
                      >
                        Éditer
                      </Button>
                      <Button
                        variant="outline"
                        size="xs"
                        icon={FaSlidersH}
                        onClick={() => onManage(club)}
                      >
                        Gérer compétitions
                      </Button>
                      <Button
                        variant="danger"
                        size="xs"
                        icon={FaTrash}
                        onClick={() => onDelete(club)}
                      >
                        Supprimer
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {/* Pagination (affichée seulement si nécessaire) */}
      {showPagination && (
        <div className="flex items-center justify-between px-4 py-3 text-sm">
          <span>
            Page <strong>{page}</strong> / {totalPages}
          </span>
          <div className="flex items-center gap-2">
            <button
              className="px-3 py-1 rounded-lg border border-gray-300 dark:border-slate-600 disabled:opacity-50"
              onClick={() => page > 1 && setPage(page - 1)}
              disabled={page <= 1}
            >
              Précédent
            </button>
            <button
              className="px-3 py-1 rounded-lg border border-gray-300 dark:border-slate-600 disabled:opacity-50"
              onClick={() => page < totalPages && setPage(page + 1)}
              disabled={page >= totalPages}
            >
              Suivant
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvitedClubTable;
